import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { toast } from 'sonner';
import { getAccessToken } from '@/lib/api';
import { useAuth } from '@/store/auth';

/**
 * Server-Sent Events subscription.
 *
 * EventSource cannot send an Authorization header, so the stream is opened
 * with fetch + a ReadableStream instead. That keeps the token out of the query
 * string — a URL is logged by every proxy and sits in browser history, which
 * is the last place an access token should be.
 *
 * Incoming events invalidate the affected React Query caches rather than
 * patching state by hand: the server stays the single source of truth and
 * there is no second copy of the data to drift.
 */

type ServerEvent =
  | 'connected' | 'heartbeat'
  | 'order.created' | 'order.status_changed' | 'order.cancelled'
  | 'notification.created' | 'target.achieved' | 'stock.low';

export function useRealtime(): { connected: boolean } {
  const queryClient = useQueryClient();
  const status = useAuth((state) => state.status);
  const [connected, setConnected] = React.useState(false);

  React.useEffect(() => {
    if (status !== 'authenticated') return;

    const controller = new AbortController();
    let retryDelay = 1000;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    async function connect(): Promise<void> {
      const token = getAccessToken();
      if (!token || cancelled) return;

      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/events`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
          credentials: 'include',
          signal: controller.signal,
        });

        if (!response.ok || !response.body) throw new Error(`SSE failed: ${response.status}`);

        setConnected(true);
        retryDelay = 1000;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Frames are separated by a blank line; anything after the last one
          // is a partial frame and must stay in the buffer.
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';

          for (const frame of frames) {
            handleFrame(frame, queryClient);
          }
        }
      } catch (error) {
        if ((error as Error)?.name === 'AbortError' || cancelled) return;

        setConnected(false);

        // Exponential backoff, capped. Without the cap a server restart has
        // every open tab hammering it on reconnect.
        retryTimer = setTimeout(() => void connect(), retryDelay);
        retryDelay = Math.min(retryDelay * 2, 30_000);
      }
    }

    void connect();

    return () => {
      cancelled = true;
      controller.abort();
      if (retryTimer) clearTimeout(retryTimer);
      setConnected(false);
    };
  }, [status, queryClient]);

  return { connected };
}

function handleFrame(frame: string, queryClient: ReturnType<typeof useQueryClient>): void {
  let eventName: ServerEvent = 'heartbeat';
  let data = '';

  for (const line of frame.split('\n')) {
    if (line.startsWith('event: ')) eventName = line.slice(7).trim() as ServerEvent;
    else if (line.startsWith('data: ')) data += line.slice(6);
    // Lines starting with ':' are keep-alive comments and are ignored.
  }

  if (eventName === 'heartbeat' || eventName === 'connected' || !data) return;

  let payload: Record<string, unknown> = {};
  try {
    payload = JSON.parse(data) as Record<string, unknown>;
  } catch {
    return;
  }

  switch (eventName) {
    case 'order.created':
    case 'order.status_changed':
    case 'order.cancelled':
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      if (eventName === 'order.created') {
        toast.info(`New order ${String(payload.orderNumber ?? '')}`.trim());
      }
      break;

    case 'notification.created':
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      if (typeof payload.title === 'string') toast.info(payload.title);
      break;

    case 'target.achieved':
      void queryClient.invalidateQueries({ queryKey: ['targets'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Target achieved!');
      break;

    case 'stock.low':
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      break;

    default:
      break;
  }
}
